import type { ReactElement } from 'react';
import { formatCurrency, formatNumber } from '@xdocs/i18n';
import type { CreditPack } from '@xdocs/schemas';
import { Button, Card, EmptyState, Skeleton, cn, useI18n } from '@xdocs/ui';
import { safeT } from '../../../i18n/safeT.js';
import { PromoCodeField } from './PromoCodeField.js';
import { useCheckoutStart } from './useCheckoutStart.js';
import { useCreditPacks } from './useCreditPacks.js';
import { usePromoRedeem } from './usePromoRedeem.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './BuyCreditsGrid.module.css';

const SKELETON_COUNT = 3;

/** Scroll target for the F48 credits-info modal's "Buy credits" CTA. */
export const BUY_CREDITS_SECTION_ID = 'settings-billing-buy-credits';

function formatPackTitle(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
}

/** F57 buy-credits grid — pack cards + shared promo field + Paddle checkout launch. */
export function BuyCreditsGrid(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();
  const { packs, isLoading, isError, onRetry } = useCreditPacks();
  const checkout = useCheckoutStart();
  const promo = usePromoRedeem('credit_pack');

  async function handleBuy(pack: CreditPack): Promise<void> {
    await checkout.start('credit_pack', {
      creditPackId: pack.id,
      ...(promo.status === 'applied' ? { promoCode: promo.code } : {}),
    });
  }

  return (
    <section id={BUY_CREDITS_SECTION_ID} className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settingsBilling:packs.title')}</h2>

      {isLoading && (
        <div aria-busy="true" className={cn(styles['grid'])}>
          <Skeleton shape="card" count={SKELETON_COUNT} />
        </div>
      )}

      {isError && (
        <EmptyState kind="error" title={t('settingsBilling:packs.status.error.title')} action={{ label: t('settingsBilling:action.retry'), onClick: onRetry }} />
      )}

      {!isLoading && !isError && packs.length === 0 && <EmptyState kind="no-results" title={t('settingsBilling:packs.status.empty.title')} />}

      {!isLoading && !isError && packs.length > 0 && (
        <>
          <div className={cn(styles['grid'])}>
            {packs.map((pack) => (
              <Card key={pack.id} className={cn(styles['packCard'])}>
                <p className={cn(styles['packTitle'])}>{formatPackTitle(pack.slug)}</p>
                <p className={cn(styles['packCredits'])}>{formatNumber(locale, pack.creditsAmount)}</p>
                {pack.complexityBlurbKey && <p className={cn(styles['packBlurb'])}>{safeT(locale, pack.complexityBlurbKey)}</p>}
                <p className={cn(styles['packPrice'])}>{formatCurrency(locale, pack.priceAmount, pack.currency)}</p>
                <Button
                  variant="primary"
                  state={checkout.isStarting ? 'loading' : 'idle'}
                  onClick={() => void handleBuy(pack)}
                >
                  {t('settingsBilling:packs.buy')}
                </Button>
              </Card>
            ))}
          </div>

          <PromoCodeField promo={promo} />

          {checkout.errorMessage && (
            <p role="alert" className={cn(styles['error'])}>
              {safeT(locale, checkout.errorMessage)}
            </p>
          )}
        </>
      )}
    </section>
  );
}
